import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Truck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import { locationHubService } from '../../services/locationHubService.js';
import { mlSheetService } from '../../services/mlSheetService.js';
import paidAmountSettlementService from '../../services/paidAmountSettlementService.js';
import { hasOgclTransferCreateAccess } from '../../utils/networkMode.js';
import { isHubDestination } from '../../utils/destinationType.js';
import { formatLocationDropdownLabel, sortLocationsAsc } from '../../utils/locationDropdownLabel.js';
import ogclTransferService from '../../services/ogclTransferService.js';

const normalizeDispatchTypes = (hubOrTypes) => {
  const raw = Array.isArray(hubOrTypes)
    ? hubOrTypes
    : (hubOrTypes?.dispatch_types ?? hubOrTypes);
  const types = Array.isArray(raw)
    ? raw
    : (raw ? String(raw).split(',') : ['OGCL']);
  return types.map((type) => String(type).trim().toUpperCase()).filter(Boolean);
};

/** Hub destination type with OGCL dispatch (OGCL-only or OGCL + ITCL). */
const isOgclTransferTargetHub = (hub) => {
  const types = new Set(normalizeDispatchTypes(hub));
  return isHubDestination(hub?.destination_type) && types.has('OGCL');
};

/** Booked shipment eligible for OGCL transfer (has OGCL, not ITCL-only). */
const isOgclEligibleReceiverHub = (hub) => {
  if (!hub) return false;
  const types = new Set(normalizeDispatchTypes(hub));
  return types.has('OGCL') && types.size > 0 && !(types.size === 1 && types.has('ITCL'));
};

const isAlreadyInOgclTransfer = (shipment) => {
  const status = String(shipment?.ogcl_transfer_status || '').toUpperCase();
  return status === 'PENDING' || status === 'APPROVED';
};

const paymentLabel = (mode) => {
  const key = String(mode || '').toLowerCase();
  if (key === 'to_pay') return 'To Pay';
  if (key === 'paid') return 'Paid';
  if (key === 'credit' || key === 'on_account') return 'Credit';
  return mode || '—';
};

const formatMoney = (value) => {
  const amount = Number(value || 0);
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const buildHubLookup = (hubRows) => {
  const map = new Map();
  (hubRows || []).forEach((hub) => {
    if (hub?.id != null) map.set(String(hub.id), hub);
  });
  return map;
};

const filterOgclShipments = (rows, hubLookup) => (
  (Array.isArray(rows) ? rows : []).filter((shipment) => {
    if (isAlreadyInOgclTransfer(shipment)) return false;
    const recvHub = shipment.receiver_location_hub
      || hubLookup.get(String(shipment.receiver_location_hub_id));
    return isOgclEligibleReceiverHub(recvHub);
  })
);

const CreateOgclTransferRequest = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreate = hasOgclTransferCreateAccess(user);
  const [hubs, setHubs] = useState([]);
  const [allHubs, setAllHubs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingShipments, setLoadingShipments] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mlCreationBlock, setMlCreationBlock] = useState({ blocked: false });
  const [formData, setFormData] = useState({
    to_hub_id: '',
    shipment_ids: [],
    vehicle_id: '',
    is_external_vehicle: false,
    vehicle_number: '',
    driver_name: '',
    remarks: '',
  });
  const hubLookupRef = useRef(new Map());
  const initialLoadDoneRef = useRef(false);

  const hubById = useMemo(() => buildHubLookup(allHubs), [allHubs]);

  const selectedShipments = useMemo(
    () => shipments.filter((s) => formData.shipment_ids.includes(s.shipment_id || s.id)),
    [shipments, formData.shipment_ids],
  );

  const selectedWithUnremittedPaid = useMemo(
    () => selectedShipments.filter((s) => Number(s.paid_remit_outstanding || 0) > 0),
    [selectedShipments],
  );

  const selectionRemitBlocked = selectedWithUnremittedPaid.length > 0;
  const createBlocked = Boolean(mlCreationBlock.blocked) || selectionRemitBlocked;

  const fetchBookedShipments = async (hubLookup) => {
    if (!user?.location_hub_id) {
      setShipments([]);
      return;
    }
    // From ITCL hub only — To hub is the receiving OGCL dispatch hub, not a destination filter.
    // for_ogcl_transfer skips ML's ITCL∩destination filter so OGCL-only dest bookings appear.
    const params = {
      from_location_ids: String(user.location_hub_id),
      for_ogcl_transfer: true,
    };
    const response = await mlSheetService.getBookedShipments(params);
    const rows = response.data?.shipments || response.data?.data || [];
    const filtered = filterOgclShipments(rows, hubLookup || hubLookupRef.current);
    setShipments(filtered);
    setFormData((prev) => ({
      ...prev,
      shipment_ids: prev.shipment_ids.filter((id) =>
        filtered.some((s) => String(s.shipment_id || s.id) === String(id)),
      ),
    }));
  };

  // Initial load once
  useEffect(() => {
    if (!canCreate) {
      Alert.error('ITCL requests are available only for ITCL hub users');
      navigate('/ogcl-transfer-requests', { replace: true });
      return undefined;
    }
    if (initialLoadDoneRef.current) return undefined;

    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const [hubsRes, allHubsRes, vehiclesRes, blockRes] = await Promise.all([
          locationHubService.getHubsForSelect({ destination_type: 'hub_destination' }),
          locationHubService.getHubsForSelect({}),
          mlSheetService.getAllVehiclesNoPage(),
          (user?.role === 'Agent' || user?.role === 'Hub Admin')
            ? paidAmountSettlementService.getMlCreationBlock().catch(() => ({ data: { blocked: false } }))
            : Promise.resolve({ data: { blocked: false } }),
        ]);
        if (cancelled) return;

        const hubRows = hubsRes.data?.hubs || hubsRes.data?.data || [];
        const allHubRows = allHubsRes.data?.hubs || allHubsRes.data?.data || hubRows;
        const vehicleRows = vehiclesRes.data?.vehicles || vehiclesRes.data?.data || vehiclesRes.data || [];
        const block = blockRes?.data || blockRes || { blocked: false };
        const lookup = buildHubLookup(allHubRows);
        hubLookupRef.current = lookup;

        setHubs(
          (Array.isArray(hubRows) ? hubRows : []).filter(
            (hub) => isOgclTransferTargetHub(hub) && String(hub.id) !== String(user?.location_hub_id),
          ),
        );
        setAllHubs(Array.isArray(allHubRows) ? allHubRows : []);
        setVehicles(Array.isArray(vehicleRows) ? vehicleRows : []);
        setMlCreationBlock(block);

        await fetchBookedShipments(lookup);
        if (!cancelled) initialLoadDoneRef.current = true;
      } catch (error) {
        if (!cancelled) {
          Alert.error(error.response?.data?.error || 'Failed to load transfer request options');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // Intentionally once on mount / when create access or hub changes
  }, [canCreate, navigate, user?.location_hub_id, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const allShipmentIds = useMemo(
    () => shipments.map((s) => s.shipment_id || s.id).filter(Boolean),
    [shipments],
  );
  const allSelected = allShipmentIds.length > 0
    && allShipmentIds.every((id) => formData.shipment_ids.includes(id));

  const toggleShipment = (shipmentId) => {
    setFormData((prev) => ({
      ...prev,
      shipment_ids: prev.shipment_ids.includes(shipmentId)
        ? prev.shipment_ids.filter((id) => id !== shipmentId)
        : [...prev.shipment_ids, shipmentId],
    }));
  };

  const toggleSelectAll = () => {
    setFormData((prev) => ({
      ...prev,
      shipment_ids: allSelected ? [] : [...allShipmentIds],
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (mlCreationBlock.blocked) {
      Alert.error(mlCreationBlock.reason || 'Settle paid collections before creating this request');
      return;
    }
    if (selectionRemitBlocked) {
      Alert.error('Selected shipment(s) still have unremitted paid amount. Submit Paid Amount Settlement first.');
      return;
    }
    if (!user?.location_hub_id) {
      Alert.error('Your account must be assigned to a hub before creating a transfer request');
      return;
    }
    if (!formData.to_hub_id || !formData.shipment_ids.length) {
      Alert.error('Select a destination hub and at least one booked shipment');
      return;
    }
    if (formData.is_external_vehicle ? !formData.vehicle_number.trim() : !formData.vehicle_id) {
      Alert.error('Select a company vehicle or enter an other vehicle number');
      return;
    }
    try {
      setSaving(true);
      await ogclTransferService.create({
        from_hub_id: user.location_hub_id,
        to_hub_id: Number(formData.to_hub_id),
        shipment_ids: formData.shipment_ids,
        vehicle_id: formData.is_external_vehicle ? null : Number(formData.vehicle_id),
        is_external_vehicle: Boolean(formData.is_external_vehicle),
        vehicle_number: formData.is_external_vehicle ? formData.vehicle_number.trim() : undefined,
        driver_name: formData.driver_name.trim() || null,
        remarks: formData.remarks.trim() || null,
      });
      Alert.success('ITCL request created');
      navigate('/ogcl-transfer-requests');
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to create ITCL request';
      Alert.error(message);
      if (/paid.*settlement|settlement.*paid|unremitted/i.test(message)) {
        try {
          const blockRes = await paidAmountSettlementService.getMlCreationBlock();
          setMlCreationBlock(blockRes?.data || { blocked: true, reason: message });
        } catch {
          setMlCreationBlock({ blocked: true, reason: message, settlement_path: '/paid-amount-settlements' });
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const settlementPath = mlCreationBlock.settlement_path || '/paid-amount-settlements';

  return (
    <DashboardLayout>
      <div className="w-full max-w-full min-w-0 mx-auto overflow-x-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-secondary flex items-center gap-2 min-w-0">
            <Truck className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" />
            <span className="leading-snug break-words">Create ITCL Request</span>
          </h1>
          <Button
            variant="secondary"
            icon={ArrowLeft}
            mobileIconOnly
            className="w-full sm:w-auto shrink-0"
            onClick={() => navigate('/ogcl-transfer-requests')}
          >
            Back
          </Button>
        </div>

        {mlCreationBlock.blocked && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 sm:px-4 py-3 text-xs sm:text-sm text-red-800">
            {(mlCreationBlock.reason || 'Settle paid collections before creating ITCL requests.')
              .replace('before creating ML sheets', 'before creating ML sheets or ITCL requests')}
            {' '}
            <Link to={settlementPath} className="font-semibold underline">
              Open Paid Amount Settlements
            </Link>
          </div>
        )}

        {selectionRemitBlocked && !mlCreationBlock.blocked && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 sm:px-4 py-3 text-xs sm:text-sm text-amber-900">
            Selected shipment(s) still have unremitted paid amount (
            {selectedWithUnremittedPaid.map((s) => s.shipment_id || s.id).join(', ')}
            ). Submit Paid Amount Settlement before creating this request.{' '}
            <Link to={settlementPath} className="font-semibold underline">
              Open Paid Amount Settlements
            </Link>
          </div>
        )}

        <form onSubmit={submit} className="bg-white rounded-xl shadow-sm p-3 sm:p-6 space-y-5 sm:space-y-6 min-w-0">
          {loading ? (
            <div className="py-12">
              <Loader fullScreen={false} size="default" message="Loading transfer options..." />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-secondary mb-2">To Hub *</label>
                  <select
                    value={formData.to_hub_id}
                    onChange={(e) => setFormData((prev) => ({ ...prev, to_hub_id: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm"
                  >
                    <option value="">Select destination hub</option>
                    {sortLocationsAsc(hubs).map((hub) => (
                      <option key={hub.id} value={hub.id}>
                        {formatLocationDropdownLabel(hub)}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[11px] sm:text-xs text-gray-500">
                    Destination hub that will unload these shipments and create the ML / dispatch.
                  </p>
                </div>

                <div className="min-w-0">
                  <label className="block text-sm font-medium text-secondary mb-2">Vehicle *</label>
                  {!formData.is_external_vehicle && (
                    <select
                      value={formData.vehicle_id}
                      onChange={(e) => setFormData((prev) => ({ ...prev, vehicle_id: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm"
                    >
                      <option value="">Select company vehicle</option>
                      {vehicles.map((vehicle) => (
                        <option key={vehicle.id || vehicle.vehicle_id} value={vehicle.id || vehicle.vehicle_id}>
                          {vehicle.vehicle_number}{vehicle.vehicle_name ? ` — ${vehicle.vehicle_name}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {formData.is_external_vehicle && (
                    <input
                      value={formData.vehicle_number}
                      onChange={(e) => setFormData((prev) => ({ ...prev, vehicle_number: e.target.value.toUpperCase() }))}
                      placeholder="Enter vehicle number"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm"
                    />
                  )}
                  <label className="flex items-center gap-2 mt-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.is_external_vehicle}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        is_external_vehicle: e.target.checked,
                        vehicle_id: '',
                        vehicle_number: '',
                      }))}
                      className="w-4 h-4 accent-orange-500"
                    />
                    Other vehicle
                  </label>
                </div>

                <div className="min-w-0">
                  <label className="block text-sm font-medium text-secondary mb-2">Driver Name</label>
                  <input
                    value={formData.driver_name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, driver_name: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm"
                  />
                </div>

                <div className="min-w-0">
                  <label className="block text-sm font-medium text-secondary mb-2">Remarks</label>
                  <input
                    value={formData.remarks}
                    onChange={(e) => setFormData((prev) => ({ ...prev, remarks: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm"
                  />
                </div>
              </div>

              <div className="min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <h2 className="font-semibold text-secondary text-sm sm:text-base">Booked Shipments *</h2>
                    <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
                      Your booked shipments for this ITCL request. Pick a To hub above to hand them over for dispatch.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 shrink-0">
                    <span className="text-xs sm:text-sm text-gray-500">
                      {formData.shipment_ids.length} selected
                    </span>
                    {shipments.length > 0 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={toggleSelectAll}
                      >
                        {allSelected ? 'Clear All' : 'Select All'}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="max-h-[min(28rem,55vh)] sm:max-h-[min(32rem,60vh)] overflow-y-auto border border-gray-200 rounded-xl divide-y min-w-0">
                  {loadingShipments ? (
                    <div className="py-10">
                      <Loader fullScreen={false} size="small" message="Loading shipments..." />
                    </div>
                  ) : shipments.length ? (
                    <>
                      <label className="flex items-center gap-3 p-3 text-sm cursor-pointer bg-gray-50 hover:bg-gray-100 sticky top-0 z-10 border-b border-gray-200">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 shrink-0 accent-orange-500"
                          aria-label="Select all shipments"
                        />
                        <span className="font-semibold text-secondary">
                          {allSelected ? 'Deselect all shipments' : 'Select all shipments'}
                        </span>
                      </label>
                      {shipments.map((shipment) => {
                    const id = shipment.shipment_id || shipment.id;
                    const destination = shipment.receiver_location
                      || shipment.receiver_location_hub?.location
                      || '';
                    const types = normalizeDispatchTypes(
                      shipment.receiver_location_hub || hubById.get(String(shipment.receiver_location_hub_id)),
                    );
                    const outstanding = Number(shipment.paid_remit_outstanding || 0);
                    const checked = formData.shipment_ids.includes(id);
                    return (
                      <label
                        key={id}
                        className={`flex items-start gap-3 p-3 text-sm cursor-pointer hover:bg-gray-50 min-w-0 ${
                          outstanding > 0 ? 'bg-amber-50/60' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleShipment(id)}
                          className="w-4 h-4 mt-1 shrink-0 accent-orange-500"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-secondary break-all">{id}</span>
                            <span className="inline-flex px-1.5 py-0.5 rounded border text-[10px] font-semibold text-gray-600 bg-gray-50">
                              {paymentLabel(shipment.mode_of_payment)}
                            </span>
                            {types.includes('OGCL') && (
                              <span className="inline-flex px-1.5 py-0.5 rounded border text-[10px] font-semibold text-blue-700 bg-blue-50 border-blue-200">
                                OGCL
                              </span>
                            )}
                          </span>
                          <span className="text-gray-600 text-xs sm:text-sm break-words block mt-1">
                            {shipment.sender_location || '—'} → {destination || '—'}
                          </span>
                          <span className="text-gray-500 text-xs block mt-0.5 break-words">
                            {shipment.sender_name || '—'} / {shipment.receiver_name || '—'}
                            {shipment.freight_charges != null ? ` · Freight ${formatMoney(shipment.freight_charges)}` : ''}
                          </span>
                          {outstanding > 0 && (
                            <span className="text-amber-800 text-xs font-medium block mt-1">
                              Unremitted paid: {formatMoney(outstanding)} — settle before create
                            </span>
                          )}
                        </span>
                      </label>
                    );
                      })}
                    </>
                  ) : (
                    <p className="p-4 text-sm text-gray-500">
                      No booked shipments available from your hub for an ITCL request. ITCL-only destination bookings are excluded.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full sm:w-auto"
                  onClick={() => navigate('/ogcl-transfer-requests')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={saving || loading || createBlocked || !formData.shipment_ids.length || !formData.to_hub_id}
                  loading={saving}
                  className="w-full sm:w-auto"
                >
                  {saving ? 'Creating…' : 'Create Request'}
                </Button>
              </div>
            </>
          )}
        </form>
      </div>
    </DashboardLayout>
  );
};

export default CreateOgclTransferRequest;
